<template>
  <div class="conf-page">
    <!-- Cabecera -->
    <div class="conf-header mb-2">
      <div class="d-flex align-items-center">
        <b-button variant="flat-secondary" class="btn-icon mr-75" @click="$router.push({ name: 'escaner-pedidos' })">
          <i class="fa-solid fa-arrow-left"></i>
        </b-button>
        <div>
          <h2 class="mb-0 font-weight-bolder">Confirmation invoice</h2>
          <small class="text-muted">
            Pedido {{ (pedido && pedido.ref) || '...' }}
            <template v-if="pedido && nombreCliente"> · {{ nombreCliente }}</template>
          </small>
        </div>
      </div>
      <b-button
        variant="primary"
        :disabled="generando || !itemsValidos.length"
        @click="generar"
      >
        <b-spinner v-if="generando" small class="mr-50" />
        <i v-else class="fa-solid fa-file-pdf mr-50"></i>
        Generar PDF
      </b-button>
    </div>

    <div v-if="cargando" class="text-center my-3">
      <b-spinner class="align-middle mr-50" /> Cargando pedido…
    </div>

    <template v-else-if="pedido">
      <!-- Confirmaciones ya emitidas -->
      <b-card v-if="previas.length" no-body class="mb-2">
        <div class="conf-card-head">
          <span class="font-weight-bold"><i class="fa-solid fa-clock-rotate-left mr-50"></i>Emitidas</span>
        </div>
        <b-card-body class="pt-75 pb-75">
          <div v-for="cf in previas" :key="cf.id" class="conf-previa">
            <span class="font-weight-bold">{{ cf.numero }}</span>
            <small class="text-muted ml-50">{{ formatFecha(cf.fecha) }}</small>
            <small v-if="cf.usuarioNombre" class="text-muted ml-50">· {{ cf.usuarioNombre }}</small>
            <b-button size="sm" variant="flat-danger" class="btn-icon ml-auto" title="Ver PDF" @click="verPdf(cf)">
              <i class="fa-solid fa-file-pdf"></i>
            </b-button>
          </div>
        </b-card-body>
      </b-card>

      <!-- Productos editables -->
      <b-card no-body>
        <div class="conf-card-head">
          <span class="font-weight-bold"><i class="fa-solid fa-box-open mr-50"></i>Productos</span>
          <div class="d-flex align-items-center">
            <label class="conf-label mb-0 mr-50">Shipping cost</label>
            <b-input-group size="sm" style="width: 150px" :prepend="(pedido && pedido.simbolo) || '£'">
              <b-form-input v-model="shipping" type="number" min="0" step="0.01" placeholder="0.00" @wheel.prevent />
            </b-input-group>
          </div>
        </div>
        <b-card-body>
          <div v-for="(it, i) in items" :key="'ci' + i" class="conf-item">
            <b-row>
              <b-col cols="12" md="5">
                <label class="conf-label">Nombre</label>
                <b-form-input v-model="it.nombre" size="sm" />
              </b-col>
              <b-col cols="6" md="4">
                <label class="conf-label">SKU</label>
                <b-form-input v-model="it.sku" size="sm" />
              </b-col>
              <b-col cols="3" md="1">
                <label class="conf-label">Cant.</label>
                <b-form-input v-model="it.cantidad" type="number" min="0" size="sm" @wheel.prevent />
              </b-col>
              <b-col cols="3" md="2">
                <label class="conf-label">Precio</label>
                <b-form-input v-model="it.precio" type="number" min="0" step="0.01" size="sm" @wheel.prevent />
              </b-col>
            </b-row>
            <b-row class="mt-25">
              <b-col cols="12" md="4">
                <label class="conf-label">Descripción</label>
                <b-form-input v-model="it.descripcion" size="sm" placeholder="Descripción del producto" />
              </b-col>
              <b-col cols="6" md="2">
                <label class="conf-label">Composición</label>
                <b-form-input v-model="it.composicion" size="sm" />
              </b-col>
              <b-col cols="6" md="2">
                <label class="conf-label">HS code</label>
                <b-form-input v-model="it.hsCode" size="sm" />
              </b-col>
              <b-col cols="6" md="1">
                <label class="conf-label">Peso</label>
                <b-form-input v-model="it.peso" size="sm" placeholder="1.2 kg" />
              </b-col>
              <b-col cols="6" md="3">
                <label class="conf-label">Barcode</label>
                <b-form-input v-model="it.barcode" size="sm" />
              </b-col>
            </b-row>
          </div>
          <small class="text-muted d-block mt-75">
            Cantidad 0 = el producto no sale en el PDF. Al generar se archiva con numeración correlativa (CI-2026-0001).
          </small>
        </b-card-body>
      </b-card>
    </template>

    <div v-else class="text-center text-muted my-3">Pedido no encontrado.</div>
  </div>
</template>

<script>
import axios from '@axios'

export default {
  data() {
    return {
      pedido: null,
      items: [],
      shipping: '',
      previas: [],
      cargando: true,
      generando: false,
    }
  },
  computed: {
    nombreCliente() {
      const c = (this.pedido && this.pedido.cliente) || {}
      return c.empresa || [c.nombre, c.apellidos].filter(Boolean).join(' ')
    },
    itemsValidos() {
      return this.items.filter(it => Number(it.cantidad) > 0)
    },
  },
  created() {
    this.cargar()
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Confirmation invoice',
        variant,
        solidColor: true,
      })
    },
    formatFecha(f) {
      if (!f) return ''
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    cargar() {
      const { id } = this.$route.params
      this.cargando = true
      axios
        .get(`/escaner/pedidos/${id}`)
        .then(({ data }) => {
          this.pedido = data
          this.items = (data.items || []).map(it => ({
            nombre: it.title || '',
            sku: it.sku || '',
            descripcion: '',
            composicion: '100% Leather',
            hsCode: '4202310010',
            peso: '',
            precio: it.wspUnit != null && it.wspUnit !== '' ? String(it.wspUnit) : '',
            cantidad: parseInt(it.qty, 10) || 0,
            barcode: '',
          }))
          return axios.get(`/escaner/pedidos/${id}/confirmaciones`)
        })
        .then(({ data }) => {
          this.previas = data || []
          const ultima = this.previas[0]
          if (ultima && Array.isArray(ultima.items) && ultima.items.length) {
            // Recupera los datos tal y como se editaron la última vez
            this.items = ultima.items.map(it => ({ ...it }))
            if (Number(ultima.shipping) > 0) this.shipping = String(ultima.shipping)
          }
          this.completarDesdeShopify()
        })
        .catch(() => {
          if (this.items.length) this.completarDesdeShopify()
        })
        .finally(() => { this.cargando = false })
    },
    // Rellena barcode, descripción y peso desde Shopify — solo los campos
    // que estén vacíos, sin pisar lo que ya se haya editado
    completarDesdeShopify() {
      const skus = this.items.map(f => f.sku).filter(Boolean)
      if (!skus.length) return
      axios
        .get('/etiquetas/lookup', { params: { sku: skus.join(',') } })
        .then(({ data }) => {
          (data || []).forEach(d => {
            const f = this.items.find(x => x.sku.toLowerCase() === (d.sku || '').toLowerCase())
            if (!f) return
            if (!f.barcode) this.$set(f, 'barcode', d.ean || '')
            if (!f.descripcion) this.$set(f, 'descripcion', d.descripcion || '')
            if (!f.peso) this.$set(f, 'peso', d.peso || '')
          })
        })
        .catch(() => {})
    },
    generar() {
      if (!this.itemsValidos.length) return
      this.generando = true
      axios
        .post(`/escaner/pedidos/${this.pedido.id}/confirmaciones`, {
          items: this.itemsValidos,
          shipping: Number(this.shipping) || 0,
        })
        .then(({ data }) => {
          this.notificar(`Confirmation invoice ${data.numero} generada`)
          this.verPdf(data)
          return axios.get(`/escaner/pedidos/${this.pedido.id}/confirmaciones`)
        })
        .then((res) => {
          if (res && res.data) this.previas = res.data
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo generar la confirmation invoice'
          this.notificar(msg, 'danger')
        })
        .finally(() => { this.generando = false })
    },
    verPdf(cf) {
      axios
        .get(`/escaner/confirmaciones/${cf.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo abrir el PDF', 'danger') })
    },
  },
}
</script>

<style lang="scss" scoped>
.conf-page {
  padding-bottom: 2rem;
}

.conf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.conf-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.conf-previa {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  margin-bottom: 4px;

  &:last-of-type { margin-bottom: 0; }
}

.conf-item {
  padding: 10px 0 12px;
  border-bottom: 1px solid #ebe9f1;

  &:last-of-type { border-bottom: none; }
}

.conf-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #b9b9c3;
  margin-bottom: 2px;
}
</style>
